import React, { useState } from 'react';
import { GoogleMap, InfoWindow } from '@react-google-maps/api';
import { Registration } from '../types';
import { format } from 'date-fns';

const mapContainerStyle = {
  width: '100%',
  height: '500px'
};

// Center coordinates for Norway and initial zoom level to show the whole country
const defaultCenter = {
  lat: 64.5,  // Slightly south to show more of southern Norway
  lng: 17.0   // Slightly east to center the view better
};

const mapOptions = {
  streetViewControl: false,
  mapTypeControl: true,
  fullscreenControl: true,
  zoomControl: true,
  mapId: import.meta.env.VITE_GOOGLE_MAPS_MAP_ID
};

interface LocationData {
  lat: number;
  lng: number;
  count: number;
  registrations: Registration[];
}

interface LicensePlateMapProps {
  registrations: Registration[];
  selectedPlate: string;
  isLoaded: boolean;
}

const LicensePlateMap: React.FC<LicensePlateMapProps> = ({ registrations, selectedPlate, isLoaded }) => {
  const [selectedMarker, setSelectedMarker] = useState<LocationData | null>(null);
  const [markers, setMarkers] = useState<google.maps.marker.AdvancedMarkerElement[]>([]);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [zoom, setZoom] = useState(4);
  const [map, setMap] = useState<google.maps.Map | null>(null);

  // Group registrations by location
  const locationData = React.useMemo(() => {
    const plateRegistrations = registrations.filter(reg => reg.licensePlate === selectedPlate);
    const locationMap = new Map<string, LocationData>();

    plateRegistrations.forEach(reg => {
      const key = `${reg.location.latitude.toFixed(4)},${reg.location.longitude.toFixed(4)}`;
      const existing = locationMap.get(key);
      
      if (existing) {
        existing.count++;
        existing.registrations.push(reg);
      } else {
        locationMap.set(key, {
          lat: reg.location.latitude,
          lng: reg.location.longitude,
          count: 1,
          registrations: [reg]
        });
      }
    });

    return Array.from(locationMap.values());
  }, [registrations, selectedPlate]);

  // Set map center to the location with most registrations
  React.useEffect(() => {
    // Always show whole Norway viewport
    setMapCenter(defaultCenter);
    setZoom(4);
  }, [locationData]);

  // Create markers when map or locationData changes
  React.useEffect(() => {
    if (!map || !isLoaded || !window.google?.maps?.marker?.AdvancedMarkerElement) return;

    // Clear existing markers
    markers.forEach(marker => {
      marker.map = null;
    });

    // Create new markers
    const newMarkers = locationData.map((location) => {
      // Create a custom pin element
      const pinElement = document.createElement('div');
      const size = Math.max(16, Math.min(32, location.count * 4));
      const color = location.count > 5 ? '#dc2626' : location.count > 2 ? '#f59e0b' : '#3b82f6';
      
      pinElement.style.cssText = `
        width: ${size}px;
        height: ${size}px;
        background-color: ${color};
        border: 2px solid white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: ${Math.max(10, size * 0.4)}px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        cursor: pointer;
      `;
      pinElement.textContent = location.count.toString();

      const marker = new window.google.maps.marker.AdvancedMarkerElement({
        position: { lat: location.lat, lng: location.lng },
        map: map,
        content: pinElement,
        title: `${location.count} registrering${location.count !== 1 ? 'er' : ''}`
      });

      // Add click listener
      marker.addListener('click', () => {
        setSelectedMarker(location);
      });

      return marker;
    });

    setMarkers(newMarkers);

    // Cleanup function
    return () => {
      newMarkers.forEach(marker => {
        marker.map = null;
      });
    };
  }, [map, locationData, isLoaded]);

  if (!isLoaded) {
    return (
      <div className="h-[500px] flex items-center justify-center bg-gray-100 rounded">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={mapCenter}
      zoom={zoom}
      options={mapOptions}
      onLoad={setMap}
      onUnmount={() => setMap(null)}
    >
      {selectedMarker && (
        <InfoWindow
          position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
          onCloseClick={() => setSelectedMarker(null)}
        >
          <div className="p-2">
            <h4 className="font-semibold text-gray-900 mb-2">
              {selectedMarker.count} registrering{selectedMarker.count !== 1 ? 'er' : ''}
            </h4>
            <p className="text-sm text-gray-600 mb-2">
              Posisjon: {selectedMarker.lat.toFixed(4)}, {selectedMarker.lng.toFixed(4)}
            </p>
            <div className="text-xs text-gray-500">
              <p>Siste registrering:</p>
              <p>
                {format(
                  new Date(
                    Math.max(...selectedMarker.registrations.map(r => r.timestamp.seconds * 1000))
                  ),
                  'dd.MM.yyyy HH:mm'
                )}
              </p>
            </div>
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  );
};

export default LicensePlateMap;